import { EAiModelStatus, ESdChangeReason, IAiModel } from "@kuika/kuika-cl-model";
import { Editor } from "@monaco-editor/react";
import { Form } from "antd/es";
import React, { createRef, ReactNode } from "react";
import { DatasourceService } from "../../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaBadge from "../../../ui-kit/kuika-badge/kuika-badge";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import { copyTextToClipboard } from "../../../ui-kit/kuika-copy-button/copy-text-to-clipboard";
import KuikaDropdownMenu from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTabs from "../../../ui-kit/kuika-tabs/kuika-tabs";
import {
  CODE_EDITOR_THEME,
  defineCodeEditorTheme,
  watchCodeEditorTheme
} from "../../code-editor-shell/code-editor-theme";
import AiModelDeprecationBanners from "../ai-model-deprecation-banners";

const formatModelLabel = (name: string): string => {
  return name
    .split("-")
    .map((part, index) => {
      if (index === 0) {
        if (part.toLowerCase() === "gpt") {
          return part.toUpperCase();
        }
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
      if (["flash", "mini", "lite", "pro", "nano"].includes(part.toLowerCase())) {
        return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
      }
      return part;
    })
    .join(" ")
    .replace(/\s+/g, " ");
};

const LLM_PROVIDER_NAMES: Record<string, string> = {
  "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F": "OpenAI",
  "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1": "Google"
};

const getModelProviderName = (model: IAiModel): string => {
  const providerId = (model as any).providerId;
  if (!providerId) return "Other";
  const normalizedId = String(providerId).toUpperCase();
  return LLM_PROVIDER_NAMES[normalizedId] ?? "Other";
};

const getModelProviderIcon = (model: IAiModel): string => {
  const providerId = (model as any).providerId;
  if (!providerId) return "";
  const normalizedId = String(providerId).toUpperCase();
  if (normalizedId === "4D9AC0E3-D3EE-49D1-B0F6-813D9A7C237F") return "openai";
  if (normalizedId === "876905BF-0E2C-4D20-84D1-D2E3B5FAFDA1") return "gemini";
  return "";
};

export interface IAIActionCustomTemplateProps {
  form: any;
  isInitial: boolean;
  isEditingTemplate?: boolean;
  model: string;
  returnType: "json" | "text";
  aiModels: IAiModel[];
  onModelChange: (model: string) => void;
  onReturnTypeChange: (returnType: "json" | "text") => void;
  requestPromptsFromAI: (value: string) => void;
  onAIPromptChange: (value: string) => void;
}

export interface IAIActionCustomTemplateState {
  editorFontSize: number;
  isEditorReady: boolean;
  isCodeWrapEnabled: boolean;
  isEditorMenuOpen: boolean;
  isUndoAvailable: boolean;
  isRedoAvailable: boolean;
  llmModels: IAiModel[];
  closedProviders: string[];
}

class AIActionCustomTemplate extends PureSdComponent<
  IAIActionCustomTemplateProps & IWithSDProps,
  IAIActionCustomTemplateState
> {
  initialSystemPromptInputRef = createRef<HTMLInputElement>();

  editorRef: any;

  monacoRef: any;

  editorContentChangeDisposable: { dispose: () => void } | undefined;

  /** Re-declares the editor's theme when the app's appearance flips. */
  codeThemeObserver: MutationObserver | undefined;

  validationErros: { message: string }[] = [];

  constructor(props) {
    super(props);
    this.state = {
      editorFontSize: 12,
      isEditorReady: false,
      isCodeWrapEnabled: false,
      isEditorMenuOpen: false,
      isUndoAvailable: false,
      isRedoAvailable: false,
      llmModels: [],
      closedProviders: []
    };
  }

  componentDidMount(): void {
    DatasourceService.GetAiModels().then((response) => {
      if (response?.data) {
        this.setState({ llmModels: response.data });
      }
    });
  }

  toggleProvider = (providerName: string) => {
    this.setState((prevState) => {
      if (prevState.closedProviders.includes(providerName)) {
        return { closedProviders: prevState.closedProviders.filter((p) => p !== providerName) };
      }
      return { closedProviders: [...prevState.closedProviders, providerName] };
    });
  };

  componentWillUnmount(): void {
    this.editorContentChangeDisposable?.dispose();
    this.codeThemeObserver?.disconnect();
  }

  handleEditorOnMount = (editor, monaco) => {
    this.editorContentChangeDisposable?.dispose();

    this.editorRef = editor;
    this.monacoRef = monaco;

    // Without a theme of its own Monaco draws its default `vs`, which on the dark theme is a
    // white slab where the JSON is. The same theme every other editor in the designer uses,
    // re-declared whenever the appearance flips.
    defineCodeEditorTheme(monaco);
    this.codeThemeObserver?.disconnect();
    this.codeThemeObserver = watchCodeEditorTheme(monaco);

    this.editorContentChangeDisposable = editor?.onDidChangeModelContent?.(() => {
      this.updateEditorHistoryAvailability();
    });

    this.setState({ isEditorReady: true });

    this.updateEditorHistoryAvailability();

    if (this.props.returnType === "json") {
      setTimeout(() => {
        this.formatEditorContent();
      }, 100);
    }
  };

  updateEditorHistoryAvailability = () => {
    const model = this.editorRef?.getModel?.();
    const isUndoAvailable = !!model?.canUndo?.();
    const isRedoAvailable = !!model?.canRedo?.();

    this.setState((prevState) => {
      if (prevState.isUndoAvailable === isUndoAvailable && prevState.isRedoAvailable === isRedoAvailable) {
        return null;
      }

      return {
        isUndoAvailable,
        isRedoAvailable
      };
    });
  };

  componentDidUpdate(prevProps: Readonly<IAIActionCustomTemplateProps & IWithSDProps>): void {
    if (prevProps.returnType !== this.props.returnType) {
      if (this.props.returnType === "json") {
        this.setState({
          isEditorReady: false,
          isUndoAvailable: false,
          isRedoAvailable: false
        });
        setTimeout(() => {
          this.formatEditorContent();
        }, 100);
      } else {
        this.editorContentChangeDisposable?.dispose();
        this.editorContentChangeDisposable = undefined;
        this.editorRef = undefined;
        this.monacoRef = undefined;
        this.setState({
          isEditorMenuOpen: false,
          isEditorReady: false,
          isUndoAvailable: false,
          isRedoAvailable: false
        });
      }
    }
  }

  getEditorOptions = () => {
    return {
      automaticLayout: true,
      fontSize: this.state.editorFontSize,
      glyphMargin: false,
      lineNumbersMinChars: 3,
      minimap: { enabled: false },
      overviewRulerBorder: false,
      padding: { top: 16, bottom: 16 },
      renderLineHighlight: "none" as const,
      scrollbar: {
        alwaysConsumeMouseWheel: true
      },
      scrollBeyondLastLine: false,
      wordWrap: this.state.isCodeWrapEnabled ? ("on" as const) : ("off" as const)
    };
  };

  closeEditorMenu = () => {
    this.setState({ isEditorMenuOpen: false });
  };

  toggleEditorMenu = (isEditorMenuOpen: boolean) => {
    this.setState({ isEditorMenuOpen });
  };

  runEditorAction = (actionName: string) => {
    const action = this.editorRef?.getAction?.(actionName);
    if (action?.run) {
      action.run();
    }
    this.editorRef?.focus?.();
  };

  runEditorCommand = async (actionNames: string[], triggerCommandName: string) => {
    this.editorRef?.focus?.();

    for (const actionName of actionNames) {
      const action = this.editorRef?.getAction?.(actionName);
      if (action?.run) {
        await action.run();
        return;
      }
    }

    this.editorRef?.trigger?.("ai-action-custom-template", triggerCommandName, null);
  };

  formatEditorContent = () => {
    this.runEditorAction("editor.action.formatDocument");
  };

  handleEditorUndo = async () => {
    await this.runEditorCommand(["undo", "editor.action.undo"], "undo");
    this.updateEditorHistoryAvailability();
  };

  handleEditorRedo = async () => {
    await this.runEditorCommand(["redo", "editor.action.redo"], "redo");
    this.updateEditorHistoryAvailability();
  };

  getEditorSelectionValue = () => {
    const selection = this.editorRef?.getSelection?.();
    const model = this.editorRef?.getModel?.();
    const hasSelection = selection && typeof selection.isEmpty === "function" && !selection.isEmpty();

    if (hasSelection) {
      return model?.getValueInRange?.(selection) || "";
    }

    return model?.getValue?.() || "";
  };

  handleCopy = async () => {
    const value = this.getEditorSelectionValue();
    if (!value) {
      return;
    }

    // The shared write, which falls back to the textarea trick when the Clipboard API is refused.
    await copyTextToClipboard(value);
    this.closeEditorMenu();
  };

  handlePaste = async () => {
    if (!this.editorRef?.getSelection || !this.editorRef?.executeEdits) {
      return;
    }

    const clipboardText = await navigator.clipboard.readText();
    if (!clipboardText?.length) {
      return;
    }

    this.editorRef.executeEdits("ai-action-custom-template", [
      {
        forceMoveMarkers: true,
        range: this.editorRef.getSelection(),
        text: clipboardText
      }
    ]);
    this.editorRef.focus();
    this.closeEditorMenu();
  };

  changeEditorFontSize = (delta: number) => {
    this.setState((prevState) => ({
      editorFontSize: Math.max(10, Math.min(18, prevState.editorFontSize + delta))
    }));
  };

  resetEditorFontSize = () => {
    this.setState({ editorFontSize: 12 });
  };

  handleCodeWrapChange = (isCodeWrapEnabled: boolean) => {
    this.setState({ isCodeWrapEnabled });
  };

  getEditorMenuOptions = () => {
    const isEditorDisabled = !this.state.isEditorReady;

    return [
      {
        disabled: isEditorDisabled,
        icon: <KuikaSvg width={16} height={16} name="copy" />,
        label: "Copy",
        onClick: this.handleCopy,
        rightSlot: "Cmd + C"
      },
      {
        disabled: isEditorDisabled,
        group: 1,
        icon: <KuikaSvg width={16} height={16} name="paste" />,
        label: "Paste",
        onClick: this.handlePaste,
        rightSlot: "Cmd + V"
      }
    ];
  };

  getEditorMenuFooter = () => {
    return (
      <div className="kuika_custom-ai-action-template__full-layout__return-type__settings-menu">
        <div
          className="kuika_custom-ai-action-template__full-layout__return-type__settings-row"
          style={{ borderBottom: "1px solid var(--dark-3)" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
          >
            <KuikaSvg width={14} height={14} name="text-size" />
            <span className="kuika_custom-ai-action-template__full-layout__return-type__settings-label">Font Size</span>
          </div>
          <div className="kuika_custom-ai-action-template__full-layout__return-type__font-controls">
            <div className="kuika_custom-ai-action-template__full-layout__return-type__font-stepper">
              <button
                className="kuika_custom-ai-action-template__full-layout__return-type__font-stepper-button"
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  this.changeEditorFontSize(-1);
                }}
                style={{ borderRight: "1px solid var(--dark-3)" }}
              >
                <KuikaSvg width={14} height={14} name="minus" />
              </button>
              <span className="kuika_custom-ai-action-template__full-layout__return-type__font-stepper-value">
                {this.state.editorFontSize}
              </span>
              <button
                className="kuika_custom-ai-action-template__full-layout__return-type__font-stepper-button"
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  this.changeEditorFontSize(1);
                }}
                style={{ borderLeft: "1px solid var(--dark-3)" }}
              >
                <KuikaSvg width={14} height={14} name="plus" color="var(--dark-6)" />
              </button>
            </div>
            <div className="kuika_custom-ai-action-template__full-layout__prompts-container__divider" />
            <button
              className="kuika_custom-ai-action-template__full-layout__return-type__font-reset-button"
              type="button"
              disabled={this.state.editorFontSize === 12}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                this.resetEditorFontSize();
              }}
            >
              <KuikaSvg width={14} height={14} name="reload" color="var(--dark-6)" />
            </button>
          </div>
        </div>
        <div className="kuika_custom-ai-action-template__full-layout__return-type__settings-row">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
          >
            <KuikaSvg width={16} height={16} name="text-wrap-column" color="var(--dark-6)" />
            <span className="kuika_custom-ai-action-template__full-layout__return-type__settings-label">Code Wrap</span>
          </div>

          <KuikaSwitch
            checked={this.state.isCodeWrapEnabled}
            onChange={this.handleCodeWrapChange}
            labelPosition="none"
          />
        </div>
        <button
          className="kuika_custom-ai-action-template__full-layout__return-type__settings-action"
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            this.formatEditorContent();
            this.closeEditorMenu();
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px"
            }}
          >
            <KuikaSvg width={16} height={16} name="arrows-horizontal" />
            <span>Tidy Up</span>
          </div>

          <KuikaBadge
            className="kuika_custom-ai-action-template__full-layout__return-type__settings-shortcut"
            content={<div style={{ fontWeight: "500" }}>Cmd + Shift + T</div>}
          />
        </button>
      </div>
    );
  };

  renderReturnTypeEditor = () => {
    if (this.props.returnType !== "json") {
      return (
        <div className="kuika_custom-ai-action-template__full-layout__return-type__text-placeholder">
          Plain text responses will be returned without a structured JSON schema.
        </div>
      );
    }

    return (
      <Form.Item
        className="kuika_custom-ai-action-template__full-layout__return-type__editor-form-item"
        name="aiReturnType"
        rules={[
          {
            validator: this.validateJSON
          }
        ]}
      >
        <Editor
          height="100%"
          language="json"
          theme={CODE_EDITOR_THEME}
          onMount={this.handleEditorOnMount}
          onChange={(value) => {
            this.props.form.current?.setFieldsValue({
              aiReturnType: value
            });
          }}
          onValidate={(markers) => {
            this.validationErros = markers;
            this.props.form.current?.validateFields(["aiReturnType"]);
          }}
          options={this.getEditorOptions()}
        />
      </Form.Item>
    );
  };

  validateJSON = async (_rule, value) => {
    if (!value?.trim()?.length) throw new Error("JSON Return Type Body is required");
    if (this.validationErros.length > 0) {
      throw new Error(this.validationErros[0].message);
    }
    const parsedJson = JSON.parse(value);

    const checkStructure = (obj, path = "") => {
      if (Array.isArray(obj)) {
        if (path === "") {
          throw new Error("Root level array detected. Please provide a JSON object at the root level.");
        }
        obj.forEach((item, index) => {
          if (Array.isArray(item)) {
            throw new Error(
              `Multidimensional array detected at ${path}[${index}]. Multidimensional arrays are not supported.`
            );
          }
          checkStructure(item, `${path}[${index}]`);
        });
      } else if (typeof obj === "object" && obj !== null) {
        const keys = Object.keys(obj);
        if (keys.length === 0) {
          const location = path === "" ? "the root level" : path.replace(/^\./, "");
          throw new Error(
            `Empty object detected at ${location}. Please add at least one property or remove the object.`
          );
        }
        keys.forEach((key) => checkStructure(obj[key], `${path}.${key}`));
      }
    };

    checkStructure(parsedJson);
  };

  getInitialLayout = () => {
    return (
      <div className="kuika_custom-ai-action-template__initial-layout">
        <div className="kuika_custom-ai-action-template__no-prompt-info">
          <div className="kuika_custom-ai-action-template__no-prompt-info__container">
            <svg width="180" height="92.15" viewBox="0 0 238 117" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_14226_93984)">
                <path
                  d="M13 24.8067C13 15.759 20.3347 8.42432 29.3824 8.42432H176.618C185.665 8.42432 193 15.759 193 24.8067V84.193C193 93.2408 185.665 100.575 176.618 100.575H13V24.8067Z"
                  fill="#F2F7FF"
                  shape-rendering="crispEdges"
                />
                <path
                  d="M14.0239 24.8067C14.0239 16.3245 20.9001 9.44822 29.3824 9.44822H176.618C185.1 9.44822 191.976 16.3245 191.976 24.8067V84.193C191.976 92.6753 185.1 99.5515 176.618 99.5515H14.0239V24.8067Z"
                  stroke="#CCDDFF"
                  strokeWidth="2.0478"
                  shape-rendering="crispEdges"
                />
                <rect
                  opacity="0.7"
                  x="29.3828"
                  y="24.8066"
                  width="147.235"
                  height="14.3346"
                  rx="7.16731"
                  fill="white"
                />
                <rect x="29.3828" y="47.3325" width="97.1907" height="14.3346" rx="7.16731" fill="white" />
                <rect x="29.3828" y="69.8584" width="40.4758" height="14.3346" rx="7.16731" fill="#CCDDFF" />
                <rect x="77.7334" y="69.8584" width="85.1056" height="14.3346" rx="7.16731" fill="white" />
                <path
                  d="M199.531 21.5122H184.219C183.089 21.5122 182.173 22.6273 182.173 24.0027V30.6644C182.173 32.0398 183.089 33.1549 184.219 33.1549H199.531C200.661 33.1549 201.577 32.0398 201.577 30.6644V24.0027C201.577 22.6273 200.661 21.5122 199.531 21.5122Z"
                  fill="#41546E"
                />
                <g filter="url(#filter1_d_14226_93984)">
                  <path
                    d="M222.276 57.7335C222.276 74.166 208.955 87.4871 192.522 87.4871C176.09 87.4871 162.769 74.166 162.769 57.7335C162.769 41.3011 170.53 27.98 192.522 27.98C214.514 27.98 222.276 41.3011 222.276 57.7335Z"
                    fill="#D9D9D9"
                  />
                  <path
                    d="M222.276 57.7335C222.276 74.166 208.955 87.4871 192.522 87.4871C176.09 87.4871 162.769 74.166 162.769 57.7335C162.769 41.3011 170.53 27.98 192.522 27.98C214.514 27.98 222.276 41.3011 222.276 57.7335Z"
                    fill="url(#paint0_linear_14226_93984)"
                  />
                </g>
                <path
                  d="M221.159 65.9889C220.044 66.5454 218.796 66.8378 217.532 66.8378H166.467C165.197 66.8378 163.949 66.5454 162.839 65.9889C160.105 64.6172 158.563 61.8513 158.946 59.0123L158.973 58.8043C160.282 49.1911 168.874 41.7986 179.402 41.2476C183.857 41.0115 188.533 40.8935 193.414 40.9216C197.236 40.944 200.93 41.0565 204.485 41.242C215.058 41.7929 223.711 49.1574 225.026 58.8043C225.037 58.8718 225.042 58.9449 225.053 59.0123C225.442 61.8513 223.894 64.6172 221.159 65.9889Z"
                  fill="#17253C"
                />
                <path
                  d="M178.538 61.9718C182.355 61.9718 185.45 58.8357 185.45 54.9671C185.45 51.0985 182.355 47.9624 178.538 47.9624C174.721 47.9624 171.627 51.0985 171.627 54.9671C171.627 58.8357 174.721 61.9718 178.538 61.9718Z"
                  fill="#24FCE2"
                />
                <path
                  d="M178.538 60.8481C181.743 60.8481 184.34 58.2154 184.34 54.9677C184.34 51.7201 181.743 49.0874 178.538 49.0874C175.334 49.0874 172.736 51.7201 172.736 54.9677C172.736 58.2154 175.334 60.8481 178.538 60.8481Z"
                  fill="url(#paint1_linear_14226_93984)"
                />
                <path
                  d="M182.257 53.4405C182.257 54.6436 181.291 55.6217 180.104 55.6217C178.917 55.6217 177.952 54.6436 177.952 53.4405C177.952 52.2375 178.917 51.2593 180.104 51.2593C181.291 51.2593 182.257 52.2375 182.257 53.4405Z"
                  fill="#EEFDFF"
                />
                <path
                  d="M204.549 61.9708C208.366 61.9708 211.461 58.8347 211.461 54.9661C211.461 51.0975 208.366 47.9614 204.549 47.9614C200.732 47.9614 197.638 51.0975 197.638 54.9661C197.638 58.8347 200.732 61.9708 204.549 61.9708Z"
                  fill="#24FCE2"
                />
                <path
                  d="M204.551 60.8471C207.755 60.8471 210.353 58.2144 210.353 54.9668C210.353 51.7191 207.755 49.0864 204.551 49.0864C201.347 49.0864 198.749 51.7191 198.749 54.9668C198.749 58.2144 201.347 60.8471 204.551 60.8471Z"
                  fill="url(#paint2_linear_14226_93984)"
                />
                <path
                  d="M208.268 53.4405C208.268 54.6436 207.303 55.6217 206.116 55.6217C204.929 55.6217 203.964 54.6436 203.964 53.4405C203.964 52.2375 204.929 51.2593 206.116 51.2593C207.303 51.2593 208.268 52.2375 208.268 53.4405Z"
                  fill="#EEFDFF"
                />
                <path
                  d="M214.513 38.3748C214.544 39.4782 213.636 40.7962 212.595 40.9091C211.619 41.0146 210.84 40.028 210.039 39.0074C209.248 38.002 207.516 35.4225 208.204 34.6919C209.112 33.7279 214.441 35.7689 214.513 38.371V38.3748Z"
                  fill="#D6C8FF"
                />
                <path
                  d="M206.105 33.1545C206.462 33.1545 206.752 32.8649 206.752 32.5077C206.752 32.1504 206.462 31.8608 206.105 31.8608C205.748 31.8608 205.458 32.1504 205.458 32.5077C205.458 32.8649 205.748 33.1545 206.105 33.1545Z"
                  fill="#D6C8FF"
                />
              </g>
              <defs>
                <filter
                  id="filter0_d_14226_93984"
                  x="0.713182"
                  y="0.171671"
                  width="236.686"
                  height="116.725"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="4.03417" />
                  <feGaussianBlur stdDeviation="6.14341" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.141176 0 0 0 0 0.427451 0 0 0 0 1 0 0 0 0.18 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_14226_93984" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_14226_93984" result="shape" />
                </filter>
                <filter
                  id="filter1_d_14226_93984"
                  x="150.773"
                  y="21.9822"
                  width="83.4978"
                  height="83.4983"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dy="5.99775" />
                  <feGaussianBlur stdDeviation="5.99775" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.141176 0 0 0 0 0.427451 0 0 0 0 1 0 0 0 0.24 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_14226_93984" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_14226_93984" result="shape" />
                </filter>
                <linearGradient
                  id="paint0_linear_14226_93984"
                  x1="156.493"
                  y1="120.064"
                  x2="257.179"
                  y2="44.5191"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0.04" stop-color="#9068FF" />
                  <stop offset="1" stop-color="#D4C9FF" />
                </linearGradient>
                <linearGradient
                  id="paint1_linear_14226_93984"
                  x1="172.731"
                  y1="54.9677"
                  x2="184.34"
                  y2="54.9677"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#030D14" />
                  <stop offset="1" stop-color="#0B2F45" />
                </linearGradient>
                <linearGradient
                  id="paint2_linear_14226_93984"
                  x1="198.743"
                  y1="54.9668"
                  x2="210.353"
                  y2="54.9668"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="#030D14" />
                  <stop offset="1" stop-color="#0B2F45" />
                </linearGradient>
              </defs>
            </svg>

            <div className="kuika_custom-ai-action-template__no-prompt-info__header">
              No prompt created, start creating!
            </div>
            <div className="kuika_custom-ai-action-template__no-prompt-info__description">
              Type a prompt and the template will be shown here accordingly. You can always customize the prompt after
              being created.
            </div>
          </div>
        </div>
        <div className="kuika_custom-ai-action-template__initial-system-prompt__container">
          <div className="kuika_custom-ai-action-template__initial-system-prompt__example">
            I want an AI template that will read receipts and return the related fields like total amount in JSON
            format.
            <br />I need tax amount, serial number, and total amount.
          </div>
          <div className="kuika_custom-ai-action-template__initial-system-prompt__input-wrapper">
            <KuikaInput
              data-testid="aiPromptInput"
              ref={this.initialSystemPromptInputRef}
              size="lg"
              placeholder="Type a prompt like shown above..."
              onKeyDown={(e) => {
                if (e.code === "Enter" && !e.shiftKey) {
                  const value = this.initialSystemPromptInputRef.current?.value;
                  if (value) this.props.requestPromptsFromAI(value);
                }
              }}
              rightIcon={
                <KuikaButton
                  data-testid="aiPromptSendButton"
                  htmlType="button"
                  size="sm"
                  type="tertiary"
                  color="neutral"
                  onClick={() => {
                    const value = this.initialSystemPromptInputRef.current?.value;
                    if (value) this.props.requestPromptsFromAI(value);
                  }}
                >
                  <KuikaSvg
                    name="arrow-up"
                    type="arrows"
                    color="var(--blue-5)"
                    width={16}
                    height={16}
                    style={{ padding: 4 }}
                  />
                </KuikaButton>
              }
            />
          </div>
        </div>
      </div>
    );
  };

  getFullLayout = () => {
    return (
      <div className="kuika_custom-ai-action-template__full-layout">
        <div
          className="kuika_custom-ai-action-template__full-layout__model-container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 8
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
              width: "100%"
            }}
          >
            <div
              className="kuika_custom-ai-action-template__full-layout__prompts-header"
              style={{
                flexDirection: "column",
                alignItems: "flex-start"
              }}
            >
              <div className="kuika_custom-ai-action-template__full-layout__prompts-header-text">
                <KuikaSvg name="sparkles" type="datasource-icons" color="var(--dark-7)" width={24} height={24} />
                <span className="kuika_custom-ai-action-template__full-layout__section-header">Choose LLM model</span>
              </div>
              <span className="kuika_custom-ai-action-template__full-layout__section-header-subtitle">
                These models support file upload:{" "}
                <b>Gemini 2.5 Flash, Gemini 2.5 Pro, GPT 5, GPT 5 Mini, GPT 5.1, GPT 5.2, GPT 5.4, GPT 5.4 Mini</b>
              </span>
            </div>

            {(() => {
              const selectedModel = this.props.aiModels.find(
                (m) => m.name === this.props.model || m.id === this.props.model
              );
              const status = selectedModel?.status as EAiModelStatus | undefined;

              let selectboxClassName: string | undefined;
              let prefixIcon: React.ReactNode = null;
              if (status === EAiModelStatus.Shutdown) {
                selectboxClassName = "kuika_custom-ai-action-template__model-selectbox--shutdown";
                prefixIcon = <KuikaSvg type="common" name="ban" width={14} height={14} color="var(--red-5)" />;
              } else if (status === EAiModelStatus.Deprecated) {
                selectboxClassName = "kuika_custom-ai-action-template__model-selectbox--deprecated";
                prefixIcon = (
                  <KuikaSvg type="common" name="alert-triangle" width={14} height={14} color="var(--yellow-7)" />
                );
              }

              const { closedProviders } = this.state;

              const filteredModels = this.state.llmModels.filter((model) => {
                // Only show InstructionModel category (0)
                const isInstructionModel = (model.categories || []).includes(0);
                if (!isInstructionModel) return false;
                const isExcluded =
                  model.name === "gemini-2.5-flash-image-preview" || model.name === "veo-3.0-fast-generate-001";
                if (isExcluded) return false;
                const isCurrentlySelected = model.name === this.props.model || model.id === this.props.model;
                if (isCurrentlySelected) return true;
                // Exclude unrecognized providers
                if (getModelProviderName(model) === "Other") return false;
                return model.status === EAiModelStatus.Active || model.status === undefined;
              });

              const groupMap = new Map<string, IAiModel[]>();
              filteredModels.forEach((model) => {
                const provider = getModelProviderName(model);
                if (!groupMap.has(provider)) groupMap.set(provider, []);
                groupMap.get(provider)!.push(model);
              });

              const selectboxOptions: Array<{
                key: string;
                value: string;
                label: React.ReactNode;
                disabled?: boolean;
              }> = [];
              groupMap.forEach((models, providerName) => {
                const isClosed = closedProviders.includes(providerName);
                selectboxOptions.push({
                  key: `__group-${providerName}`,
                  value: `__group-${providerName}`,
                  label: (
                    <span
                      className="kuika_custom-ai-action-template__model-option-group-header"
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        this.toggleProvider(providerName);
                      }}
                    >
                      <span
                        className={`kuika_custom-ai-action-template__model-option-chevron${
                          isClosed ? "" : " kuika_custom-ai-action-template__model-option-chevron--open"
                        }`}
                      >
                        <KuikaSvg type="arrows" name="chevron-down" width={12} height={12} color="var(--blue-5)" />
                      </span>
                      {providerName}
                    </span>
                  ),
                  disabled: true
                });
                if (!isClosed) {
                  models.forEach((model) => {
                    const iconName = getModelProviderIcon(model);
                    selectboxOptions.push({
                      key: model.id || model.name,
                      value: model.name,
                      label: (
                        <span
                          className="kuika_custom-ai-action-template__model-option-label"
                          style={{
                            gap: "6px"
                          }}
                        >
                          {iconName && (
                            <div
                              style={{
                                gap: "6px",
                                display: "flex",
                                alignItems: "center"
                              }}
                            >
                              <KuikaSvg type="agent-icons" name={iconName} width={12} height={12} useOriginalColors />
                            </div>
                          )}
                          {formatModelLabel(model.name)}
                        </span>
                      )
                    });
                  });
                }
              });

              return (
                <KuikaSelectbox
                  value={this.props.model}
                  onChange={(e) => this.props.onModelChange(e as string)}
                  className={selectboxClassName}
                  prefix={prefixIcon}
                  options={selectboxOptions}
                />
              );
            })()}
          </div>
          {(() => {
            const selectedModel = this.props.aiModels.find(
              (m) => m.name === this.props.model || m.id === this.props.model
            );
            const status = selectedModel?.status as EAiModelStatus | undefined;
            const shutdownItems = status === EAiModelStatus.Shutdown ? [this.props.model] : [];
            const deprecatedItems = status === EAiModelStatus.Deprecated ? [this.props.model] : [];
            return (
              <AiModelDeprecationBanners
                shutdownItems={shutdownItems}
                deprecatedItems={deprecatedItems}
                variant="editor"
              />
            );
          })()}
        </div>

        <div className="kuika_custom-ai-action-template__full-layout__prompts-header-container">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: 24
            }}
          >
            <div className="kuika_custom-ai-action-template__full-layout__prompts-header">
              <KuikaSvg name="user-code" type="common" color="var(--dark-7)" width={24} height={24} />
              <span className="kuika_custom-ai-action-template__full-layout__section-header">User Prompt</span>
            </div>
            <div className="kuika_custom-ai-action-template__full-layout__prompts-header">
              <KuikaSvg name="device-imac-code" type="common" color="var(--dark-7)" width={24} height={24} />
              <span className="kuika_custom-ai-action-template__full-layout__section-header">System Prompt</span>
            </div>
          </div>

          <div className="kuika_custom-ai-action-template__full-layout__prompts-container">
            <Form.Item name="aiPrompt" noStyle>
              <textarea
                onChange={(e) => {
                  // Always extract parameters when user types in the prompt
                  this.props.onAIPromptChange(e.target.value);
                }}
                draggable={false}
              />
            </Form.Item>
            {/* <div className="kuika_custom-ai-action-template__full-layout__prompts-container__divider" /> */}
            <Form.Item name="systemPrompt" noStyle>
              <textarea draggable={false} />
            </Form.Item>
          </div>
        </div>
        <div className="kuika_custom-ai-action-template__full-layout__return-type__container">
          <div className="kuika_custom-ai-action-template__full-layout__return-type__description-container">
            <div className="kuika_custom-ai-action-template__full-layout__return-type__header-copy">
              <span className="kuika_custom-ai-action-template__full-layout__return-type__section-header">
                <KuikaSvg name="code" type="common" color="var(--dark-7)" width={24} height={24} />
                Sample Output Data
              </span>
              <span className="kuika_custom-ai-action-template__full-layout__return-type__description">
                Choose how the AI should return its response. Either as plain text or select JSON to design your own
                structured data for detailed results.
                {/* <KuikaButton
                  className="kuika_custom-ai-action-template__full-layout__return-type__learn-more"
                  htmlType="button"
                  size="sm"
                  type="text"
                >
                  Learn More
                </KuikaButton> */}
              </span>
            </div>
          </div>
          <div className="kuika_custom-ai-action-template__full-layout__return-type__editor-shell">
            <div className="kuika_custom-ai-action-template__full-layout__return-type__toolbar">
              <div className="kuika_custom-ai-action-template__full-layout__return-type__tabs-wrapper">
                <KuikaTabs
                  size="lg"
                  onChange={(value) => this.props.onReturnTypeChange(value as "text" | "json")}
                  options={[
                    { label: "JSON", value: "json" },
                    { label: "Text", value: "text" }
                  ]}
                  value={this.props.returnType}
                />
              </div>
              <div className="kuika_custom-ai-action-template__full-layout__return-type__toolbar-actions">
                <KuikaButton
                  className="kuika_custom-ai-action-template__full-layout__return-type__toolbar-button"
                  disabled={!this.state.isEditorReady || !this.state.isUndoAvailable}
                  isIcon
                  onClick={this.handleEditorUndo}
                  type="tertiary"
                >
                  <KuikaSvg
                    color={this.state.isEditorReady && this.state.isUndoAvailable ? "var(--blue-5)" : "var(--dark-5)"}
                    height={16}
                    name="arrow-back-up"
                    type="arrows"
                    width={16}
                  />
                </KuikaButton>
                <KuikaButton
                  className="kuika_custom-ai-action-template__full-layout__return-type__toolbar-button"
                  disabled={!this.state.isEditorReady || !this.state.isRedoAvailable}
                  isIcon
                  onClick={this.handleEditorRedo}
                  type="tertiary"
                >
                  <KuikaSvg
                    color={this.state.isEditorReady && this.state.isRedoAvailable ? "var(--blue-5)" : "var(--dark-5)"}
                    height={16}
                    name="arrow-forward-up"
                    type="arrows"
                    width={16}
                  />
                </KuikaButton>
                <KuikaDropdownMenu
                  closeOnSelect={false}
                  footer={this.getEditorMenuFooter()}
                  offset={[0, 6]}
                  open={this.state.isEditorMenuOpen}
                  options={this.getEditorMenuOptions()}
                  overlayClassName="kuika_custom-ai-action-template__full-layout__return-type__settings-dropdown"
                  placement="bottomRight"
                  setOpen={this.toggleEditorMenu}
                  showDividerAfter={false}
                >
                  <KuikaButton
                    active={this.state.isEditorMenuOpen}
                    className="kuika_custom-ai-action-template__full-layout__return-type__toolbar-button"
                    isIcon
                    type="tertiary"
                  >
                    <KuikaSvg
                      color={this.state.isEditorMenuOpen ? "var(--blue-6)" : "var(--dark-7)"}
                      height={16}
                      name="settings"
                      type="sidebar-icons"
                      width={16}
                    />
                  </KuikaButton>
                </KuikaDropdownMenu>
              </div>
            </div>
            <div className="kuika_custom-ai-action-template__full-layout__return-type__editor-panel">
              {this.renderReturnTypeEditor()}
            </div>
          </div>
        </div>
      </div>
    );
  };

  render(): ReactNode {
    return (
      <div className="kuika_custom-ai-action-template__container">
        {this.props.isInitial ? this.getInitialLayout() : this.getFullLayout()}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const aIActionCustomTemplate = withSD<IAIActionCustomTemplateProps>(AIActionCustomTemplate);
export { aIActionCustomTemplate as AIActionCustomTemplate };
